import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutGrid, List, Search, Sparkles, SlidersHorizontal, X, BellRing, Plus, Minus } from "lucide-react";
import { api, fileUrl, fmtEur, getErrorMessage } from "../../api";
import { useCart } from "../../CartContext";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "../../components/ui/sheet";
import PropagationBadge from "../../components/PropagationBadge";
import FloatingCartSummary from "../../components/FloatingCartSummary";
import { toast } from "sonner";

export default function Catalog() {
  const { cart, setItem } = useCart();
  const [layout, setLayout] = useState(() => {
    if (typeof window === "undefined") return "card";
    return localStorage.getItem("catalog_layout") || "card";
  });
  // Persist layout choice across sessions.
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("catalog_layout", layout);
  }, [layout]);
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sizeOptId, setSizeOptId] = useState("");
  const [ownCross, setOwnCross] = useState(false);
  const [propagationType, setPropagationType] = useState(""); // "" | "seed_derived" | "clone"
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState("name");
  const [selectedParents, setSelectedParents] = useState([]);

  const [categories, setCategories] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);
  const [parents, setParents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (categoryId) params.set("category_id", categoryId);
    if (sizeOptId) params.set("size_option_id", sizeOptId);
    if (ownCross) params.set("is_own_cross", "true");
    if (propagationType) params.set("propagation_type", propagationType);
    if (inStock) params.set("in_stock", "true");
    if (selectedParents.length) params.set("parents", selectedParents.join(","));
    params.set("sort", sort);
    try {
      const { data } = await api.get(`/catalog?${params.toString()}`);
      setProducts(data.products || []);
    } finally { setLoading(false); }
  }, [q, categoryId, sizeOptId, ownCross, propagationType, inStock, selectedParents, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/size-options").then((r) => setSizeOpts(r.data)).catch(() => {});
    api.get("/catalog/parents").then((r) => setParents(r.data)).catch(() => {});
  }, []);

  const cartQtyBySv = useMemo(() => Object.fromEntries((cart?.items || []).map((i) => [i.size_variant_id, i.quantity])), [cart]);

  const clearFilters = () => { setQ(""); setCategoryId(""); setSizeOptId(""); setOwnCross(false); setPropagationType(""); setInStock(false); setSelectedParents([]); };
  const anyFilter = q || categoryId || sizeOptId || ownCross || propagationType || inStock || selectedParents.length > 0;
  // Count only sheet-managed filters (search + sort are always in the sticky bar)
  const sheetFilterCount = (categoryId ? 1 : 0) + (sizeOptId ? 1 : 0) + (ownCross ? 1 : 0) + (propagationType ? 1 : 0) + (inStock ? 1 : 0) + selectedParents.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="db-h mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="catalog-title">Catalog</h1>
        <p className="text-sm text-[#5C665A]">Your country- and account-specific pricing is shown live.</p>
      </div>

      {/* Mobile sticky filter row (<md): Filters button + search + sort */}
      <div className="md:hidden bg-white border border-[#E2E4E0] rounded-lg p-2 mb-3 sticky top-14 z-10 flex items-center gap-2">
        <MobileFilterSheet
          categoryId={categoryId} setCategoryId={setCategoryId}
          sizeOptId={sizeOptId} setSizeOptId={setSizeOptId}
          ownCross={ownCross} setOwnCross={setOwnCross}
          propagationType={propagationType} setPropagationType={setPropagationType}
          inStock={inStock} setInStock={setInStock}
          parents={parents}
          selectedParents={selectedParents} setSelectedParents={setSelectedParents}
          categories={categories} sizeOpts={sizeOpts}
          count={sheetFilterCount}
          onClearAll={clearFilters}
        />
        <div className="relative flex-1 min-w-0">
          <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
          <Input data-testid="catalog-search-m" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-7 h-10 w-full" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-10 w-24 shrink-0" data-testid="catalog-sort-m"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop filter bar (md+): full row */}
      <div className="hidden md:block bg-white border border-[#E2E4E0] rounded-lg p-3 mb-4 sticky top-14 z-10">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-[#5C665A]" />
            <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-7 h-9 w-56" />
          </div>
          <Select value={categoryId || "__all__"} onValueChange={(v) => setCategoryId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-40" data-testid="catalog-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sizeOptId || "__all__"} onValueChange={(v) => setSizeOptId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-36" data-testid="catalog-filter-size"><SelectValue placeholder="Size" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sizes</SelectItem>
              {sizeOpts.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}{s.note ? ` (${s.note})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-32" data-testid="catalog-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="newest">Sort: Newest</SelectItem>
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer h-9 px-2">
            <Checkbox data-testid="catalog-filter-cross" checked={ownCross} onCheckedChange={(v) => setOwnCross(!!v)} />
            <Sparkles size={13} /> DocBlock Cross
          </label>
          <Select value={propagationType || "__all__"} onValueChange={(v) => setPropagationType(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-9 w-40" data-testid="catalog-filter-propagation"><SelectValue placeholder="Propagation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All propagation</SelectItem>
              <SelectItem value="seed_derived">Seed Derived</SelectItem>
              <SelectItem value="clone">Clone</SelectItem>
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer h-9 px-2">
            <Checkbox data-testid="catalog-filter-instock" checked={inStock} onCheckedChange={(v) => setInStock(!!v)} />
            In stock
          </label>
          <div className="ml-auto flex items-center gap-1 border border-[#E2E4E0] rounded-md p-0.5">
            <button
              className={`h-8 w-8 grid place-items-center rounded ${layout === "table" ? "bg-[#E9F5EC] text-[#1B4332]" : "text-[#5C665A] hover:bg-[#F9F9F8]"}`}
              onClick={() => setLayout("table")} data-testid="catalog-layout-table" title="Table view"
            ><List size={14} /></button>
            <button
              className={`h-8 w-8 grid place-items-center rounded ${layout === "card" ? "bg-[#E9F5EC] text-[#1B4332]" : "text-[#5C665A] hover:bg-[#F9F9F8]"}`}
              onClick={() => setLayout("card")} data-testid="catalog-layout-card" title="Card view"
            ><LayoutGrid size={14} /></button>
          </div>
        </div>
        {/* Parents multi-select */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[#5C665A]">Parents / crosses:</span>
          <ParentMultiSelect options={parents} value={selectedParents} onChange={setSelectedParents} />
          {anyFilter && (
            <button onClick={clearFilters} className="text-xs text-[#5C665A] hover:text-[#1A1D1A] inline-flex items-center gap-1 ml-2" data-testid="catalog-clear-filters">
              <X size={12} /> Clear filters
            </button>
          )}
          <div className="ml-auto text-xs text-[#5C665A]" data-testid="catalog-count">{products.length} product{products.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      {/* Mobile: active-filter chips + product count under sticky row */}
      <div className="md:hidden flex items-center justify-between gap-2 mb-3 text-xs text-[#5C665A]">
        {anyFilter ? (
          <button onClick={clearFilters} data-testid="catalog-clear-filters-m" className="inline-flex items-center gap-1 hover:text-[#1A1D1A]">
            <X size={12} /> Clear filters
          </button>
        ) : <span />}
        <div data-testid="catalog-count-m">{products.length} product{products.length === 1 ? "" : "s"}</div>
      </div>

      {loading && <div className="p-8 text-center text-[#5C665A]" data-testid="catalog-loading">Loading catalog…</div>}
      {!loading && products.length === 0 && (
        <div className="p-12 text-center bg-white border border-[#E2E4E0] rounded-lg text-[#5C665A]" data-testid="catalog-empty">
          No products match your filters.
        </div>
      )}

      {!loading && products.length > 0 && layout === "table" && (
        <TableView products={products} cartQty={cartQtyBySv} cartItems={cart.items || []} onSet={setItem} />
      )}
      {!loading && products.length > 0 && layout === "card" && (
        <CardView products={products} cartQty={cartQtyBySv} cartItems={cart.items || []} onSet={setItem} />
      )}

      <FloatingCartSummary />
    </div>
  );
}

/* -------------------- Mobile filter sheet -------------------- */

function MobileFilterSheet({
  categoryId, setCategoryId,
  sizeOptId, setSizeOptId,
  ownCross, setOwnCross,
  propagationType, setPropagationType,
  inStock, setInStock,
  parents, selectedParents, setSelectedParents,
  categories, sizeOpts,
  count, onClearAll,
}) {
  const [open, setOpen] = useState(false);
  // Local draft state so users can Apply/Cancel
  const [draft, setDraft] = useState({ categoryId, sizeOptId, ownCross, propagationType, inStock, selectedParents });
  useEffect(() => {
    if (open) setDraft({ categoryId, sizeOptId, ownCross, propagationType, inStock, selectedParents });
  }, [open, categoryId, sizeOptId, ownCross, propagationType, inStock, selectedParents]);

  const apply = () => {
    setCategoryId(draft.categoryId);
    setSizeOptId(draft.sizeOptId);
    setOwnCross(draft.ownCross);
    setPropagationType(draft.propagationType);
    setInStock(draft.inStock);
    setSelectedParents(draft.selectedParents);
    setOpen(false);
  };
  const clearAll = () => {
    setDraft({ categoryId: "", sizeOptId: "", ownCross: false, propagationType: "", inStock: false, selectedParents: [] });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="catalog-filters-trigger"
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#E2E4E0] bg-white text-sm text-[#1A1D1A] hover:bg-[#F9F9F8] active:scale-95 transition shrink-0"
        >
          <SlidersHorizontal size={15} />
          <span>Filters</span>
          {count > 0 && (
            <span
              data-testid="catalog-filters-count"
              className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-[#2D6A4F] text-white text-[11px] px-1.5 db-mono"
            >
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="p-0 h-[85vh] rounded-t-2xl flex flex-col [&>button.absolute]:hidden"
        data-testid="catalog-filters-sheet"
      >
        <div className="px-4 h-14 flex items-center justify-between border-b border-[#E2E4E0] shrink-0">
          <SheetTitle className="text-base db-h font-semibold">Filters</SheetTitle>
          <SheetClose asChild>
            <button
              type="button"
              aria-label="Close filters"
              data-testid="catalog-filters-close"
              className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#E2E4E0] bg-white text-[#1A1D1A] hover:bg-[#F9F9F8] active:scale-95 transition"
            >
              <X size={18} />
            </button>
          </SheetClose>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Category */}
          <div>
            <label className="text-xs text-[#5C665A] uppercase tracking-wide db-h font-semibold">Category</label>
            <Select value={draft.categoryId || "__all__"} onValueChange={(v) => setDraft((d) => ({ ...d, categoryId: v === "__all__" ? "" : v }))}>
              <SelectTrigger className="h-10 mt-1" data-testid="catalog-filters-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Size */}
          <div>
            <label className="text-xs text-[#5C665A] uppercase tracking-wide db-h font-semibold">Size</label>
            <Select value={draft.sizeOptId || "__all__"} onValueChange={(v) => setDraft((d) => ({ ...d, sizeOptId: v === "__all__" ? "" : v }))}>
              <SelectTrigger className="h-10 mt-1" data-testid="catalog-filters-size"><SelectValue placeholder="Size" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sizes</SelectItem>
                {sizeOpts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{s.note ? ` (${s.note})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Propagation */}
          <div>
            <label className="text-xs text-[#5C665A] uppercase tracking-wide db-h font-semibold">Propagation</label>
            <Select value={draft.propagationType || "__all__"} onValueChange={(v) => setDraft((d) => ({ ...d, propagationType: v === "__all__" ? "" : v }))}>
              <SelectTrigger className="h-10 mt-1" data-testid="catalog-filters-propagation"><SelectValue placeholder="Propagation" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All propagation</SelectItem>
                <SelectItem value="seed_derived">Seed Derived</SelectItem>
                <SelectItem value="clone">Clone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggles */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 h-11 px-2 rounded-md hover:bg-[#F9F9F8] cursor-pointer">
              <Checkbox
                data-testid="catalog-filters-cross"
                checked={draft.ownCross}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, ownCross: !!v }))}
              />
              <Sparkles size={14} className="text-[#2D6A4F]" />
              <span className="text-sm">DocBlock Cross only</span>
            </label>
            <label className="flex items-center gap-2 h-11 px-2 rounded-md hover:bg-[#F9F9F8] cursor-pointer">
              <Checkbox
                data-testid="catalog-filters-instock"
                checked={draft.inStock}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, inStock: !!v }))}
              />
              <span className="text-sm">In stock only</span>
            </label>
          </div>

          {/* Parents multi-select */}
          <div>
            <label className="text-xs text-[#5C665A] uppercase tracking-wide db-h font-semibold">Parents / crosses</label>
            <div className="mt-1">
              <ParentMultiSelect
                options={parents}
                value={draft.selectedParents}
                onChange={(v) => setDraft((d) => ({ ...d, selectedParents: v }))}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[#E2E4E0] p-3 flex items-center gap-2 shrink-0 bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={() => { clearAll(); onClearAll(); setOpen(false); }}
            data-testid="catalog-filters-clear"
            className="h-11 flex-1"
          >
            Clear all
          </Button>
          <Button
            type="button"
            onClick={apply}
            data-testid="catalog-filters-apply"
            className="h-11 flex-1 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
          >
            Apply
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* -------------------- Parent multi-select -------------------- */

function ParentMultiSelect({ options, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = options.filter((o) => !value.includes(o) && (!q || o.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="relative inline-block">
      <div className="inline-flex items-center gap-1 flex-wrap">
        {value.map((p) => (
          <Badge key={p} className="bg-[#E9F5EC] text-[#1B4332] border-transparent" data-testid={`catalog-parent-chip-${p}`}>
            {p}
            <button className="ml-1 hover:text-[#C94A4A]" onClick={() => onChange(value.filter((x) => x !== p))}><X size={11} /></button>
          </Badge>
        ))}
        <div className="relative">
          <Input
            data-testid="catalog-parent-input"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={value.length ? "" : "Add parent…"}
            className="h-8 w-40 text-xs"
          />
          {open && filtered.length > 0 && (
            <div className="absolute z-20 mt-1 bg-white border border-[#E2E4E0] rounded-md shadow-sm max-h-52 overflow-auto w-56" data-testid="catalog-parent-options">
              {filtered.slice(0, 20).map((o) => (
                <button
                  key={o} type="button"
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-[#F9F9F8]"
                  onMouseDown={(e) => { e.preventDefault(); onChange([...value, o]); setQ(""); }}
                  data-testid={`catalog-parent-option-${o}`}
                >{o}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------- Lineage line -------------------- */

function LineageLine({ p }) {
  if (!p.is_own_cross || (!p.seed_parent && !p.pollen_parent)) return null;
  const roles = p.matched_parent_roles || [];
  const link = (parent) => {
    if (!parent) return <span className="text-[#5C665A]">—</span>;
    if (parent.linked_product_id) {
      return <Link to={`/catalog/${parent.linked_product_id}`} className="underline underline-offset-2 hover:text-[#1B4332]" data-testid={`lineage-link-${parent.linked_product_id}`}>{parent.name}</Link>;
    }
    return <span>{parent.name}</span>;
  };
  return (
    <div className="text-xs text-[#5C665A] mt-0.5 flex flex-wrap items-center gap-x-1 gap-y-0.5">
      <span className="inline-flex items-center">
        <RoleTag role="seed" active={roles.includes("seed")} />
        {link(p.seed_parent)}
      </span>
      <span className="opacity-60">×</span>
      <span className="inline-flex items-center">
        <RoleTag role="pollen" active={roles.includes("pollen")} />
        {link(p.pollen_parent)}
      </span>
    </div>
  );
}

function RoleTag({ role, active }) {
  return (
    <span
      data-testid={`lineage-role-${role}${active ? "-match" : ""}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider mr-1 db-h font-semibold ${
        active ? "bg-[#2D6A4F] text-white" : "bg-[#F0EEE7] text-[#5C665A]"
      }`}
    >
      {role}
    </span>
  );
}

/* -------------------- Size row (shared) -------------------- */

function TierBreaksLine({ breaks }) {
  return (
    <div className="text-[11px] db-mono text-[#5C665A]" data-testid="tier-breaks">
      {breaks.map((b, i) => (
        <span key={i}>
          {b.min_qty}{b.max_qty ? `–${b.max_qty}` : "+"}: {fmtEur(b.unit_price)}
          {i < breaks.length - 1 ? " · " : ""}
        </span>
      ))}
    </div>
  );
}

function StockPill({ size }) {
  const isIn = size.in_stock ?? (size.stock_qty ?? 0) > 0;
  if (!isIn) return <span className="text-xs text-[#C94A4A] db-mono">sold out</span>;
  // Exact mode: real numeric visibility.
  if (size.stock_qty != null) {
    if (size.stock_qty <= 5) return <span className="text-xs text-[#D4A373] db-mono">only {size.stock_qty} left</span>;
    return <span className="text-xs text-[#5C665A] db-mono">{size.stock_qty} in stock</span>;
  }
  // Banded mode: server banded number (small, controlled).
  if (size.display_qty != null) {
    return <span className="text-xs text-[#5C665A] db-mono">{size.display_qty}+ available</span>;
  }
  return <span className="text-xs text-[#5C665A] db-mono">in stock</span>;
}

function priceForQty(breaks, qty) {
  if (!qty || qty < 1) qty = 1;
  const hit = breaks.find((b) => qty >= b.min_qty && (b.max_qty == null || qty <= b.max_qty));
  return hit ? hit.unit_price : breaks[0]?.unit_price ?? 0;
}

function QtyStepper({ size, current, onChange, testidBase, moreRequested = false }) {
  const isIn = size.in_stock ?? (size.stock_qty ?? 0) > 0;
  const [askMore, setAskMore] = useState(false);
  if (!isIn) return <NotifyMeButton sizeVariantId={size.size_variant_id} />;

  // Cap logic: banded → display_qty; exact → stock_qty; else no cap.
  const bandedCap = size.display_qty ?? null;
  const exactCap = size.stock_qty ?? null;
  const cap = exactCap ?? bandedCap ?? Number.POSITIVE_INFINITY;
  const isBanded = bandedCap != null && exactCap == null;

  const setQ = (q) => {
    if (moreRequested && isBanded) {
      // Once the buyer is in "requested" mode, respect their number; dropping
      // back below the banded cap flips them out of that flow via the server.
      onChange(Math.max(0, q), q > bandedCap);
      return;
    }
    onChange(Math.max(0, Math.min(cap, q)), false);
  };

  const atCap = isBanded && current >= bandedCap && !moreRequested;

  return (
    <>
      <div className="inline-flex items-center gap-1" data-testid={`${testidBase}-stepper-${size.size_variant_id}`}>
        <button className="w-7 h-7 rounded-md border border-[#E2E4E0] grid place-items-center hover:bg-[#F9F9F8] disabled:opacity-40" onClick={() => setQ(current - 1)} disabled={current <= 0} data-testid={`${testidBase}-dec-${size.size_variant_id}`}><Minus size={13} /></button>
        <input
          type="number" min={0}
          value={current}
          onChange={(e) => setQ(Number(e.target.value) || 0)}
          className="db-inline w-14 text-center db-mono"
          data-testid={`${testidBase}-qty-${size.size_variant_id}`}
        />
        <button
          className="w-7 h-7 rounded-md border border-[#E2E4E0] grid place-items-center hover:bg-[#F9F9F8] disabled:opacity-40"
          onClick={() => {
            if (atCap) { setAskMore(true); return; }
            setQ(current + 1);
          }}
          disabled={!atCap && current >= cap}
          data-testid={`${testidBase}-inc-${size.size_variant_id}`}
        ><Plus size={13} /></button>
      </div>
      {atCap && (
        <button
          type="button"
          onClick={() => setAskMore(true)}
          className="ml-2 text-[11px] underline text-[#2D6A4F] hover:text-[#1B4332]"
          data-testid={`${testidBase}-more-${size.size_variant_id}`}
        >
          Looking for more?
        </button>
      )}
      {moreRequested && (
        <span className="ml-2 text-[11px] text-[#5C665A] db-mono" data-testid={`${testidBase}-requested-${size.size_variant_id}`}>
          requested
        </span>
      )}
      {askMore && (
        <MoreDialog
          size={size}
          current={current}
          onCancel={() => setAskMore(false)}
          onSubmit={(desired) => {
            setAskMore(false);
            onChange(desired, true);
          }}
        />
      )}
    </>
  );
}

function MoreDialog({ size, current, onCancel, onSubmit }) {
  const [value, setValue] = useState(Math.max(current + 1, (size.display_qty || 1) + 1));
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      data-testid={`more-dialog-${size.size_variant_id}`}
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg border border-[#E2E4E0] p-5 w-full max-w-sm shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-h font-semibold text-base">Looking for more?</div>
        <p className="text-sm text-[#5C665A] mt-1">
          How many are you looking to buy? We'll confirm availability after you place the request.
        </p>
        <div className="mt-3">
          <input
            type="number"
            min={Math.max(current + 1, 1)}
            value={value}
            onChange={(e) => setValue(Math.max(1, Number(e.target.value) || 0))}
            className="db-inline w-full text-center h-10 text-lg db-mono"
            data-testid={`more-dialog-input-${size.size_variant_id}`}
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="h-9 px-3 rounded-md border border-[#E2E4E0] text-sm hover:bg-[#F9F9F8]"
            onClick={onCancel}
            data-testid={`more-dialog-cancel-${size.size_variant_id}`}
          >Cancel</button>
          <button
            className="h-9 px-3 rounded-md bg-[#2D6A4F] text-white text-sm hover:bg-[#1B4332] disabled:opacity-40"
            onClick={() => onSubmit(Number(value))}
            disabled={!value || Number(value) < 1}
            data-testid={`more-dialog-submit-${size.size_variant_id}`}
          >Request {value}</button>
        </div>
      </div>
    </div>
  );
}

function NotifyMeButton({ sizeVariantId }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border ${done ? "border-[#40916C] text-[#40916C] bg-[#E9F5EC]" : "border-[#E2E4E0] text-[#5C665A] hover:bg-[#F9F9F8]"}`}
      onClick={async () => {
        try { await api.post("/notify-me", { size_variant_id: sizeVariantId }); setDone(true); toast.success("We'll notify you when it's back."); }
        catch { toast.error("Failed"); }
      }}
      data-testid={`notify-me-${sizeVariantId}`}
    >
      <BellRing size={12} /> {done ? "Subscribed" : "Notify me"}
    </button>
  );
}

/* -------------------- Card view -------------------- */

function CardView({ products, cartQty, cartItems, onSet }) {
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-card-view">
      {products.map((p) => (
        <div key={p.id} className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden" data-testid={`catalog-card-${p.id}`}>
          <Link to={`/catalog/${p.id}`} className="block aspect-[4/3] bg-[#F9F9F8] overflow-hidden grid place-items-center text-[#B7C0B4]">
            {p.photos?.[0]
              ? <img src={fileUrl(p.photos[0].storage_path)} alt="" className="w-full h-full object-contain hover:scale-[1.03] transition-transform duration-500" data-testid={`catalog-card-image-${p.id}`} />
              : <Sparkles size={28} strokeWidth={1.25} aria-hidden="true" />}
          </Link>
          <div className="p-3">
            <div className="min-w-0">
              <Link
                to={`/catalog/${p.id}`}
                className="db-h font-semibold text-[15px] leading-tight hover:underline block break-words"
                data-testid={`catalog-card-name-${p.id}`}
              >
                {p.name}
              </Link>
              <LineageLine p={p} />
              {p.is_own_cross && (
                <Badge className="mt-1 bg-[#E9F5EC] text-[#1B4332] border-transparent">DocBlock Cross</Badge>
              )}
              <PropagationBadge type={p.propagation_type} className="mt-1 ml-1" testid={`catalog-card-propagation-${p.id}`} />
            </div>
            <div className="mt-3 space-y-2">
              {p.sizes.map((s) => {
                const cartItem = cartItems.find((i) => i.size_variant_id === s.size_variant_id);
                return (
                  <SizeInlineRow
                    key={s.size_variant_id} size={s}
                    qty={cartQty[s.size_variant_id] || 0}
                    moreRequested={!!cartItem?.more_requested}
                    onSet={(q, more) => onSet(s.size_variant_id, q, { more_requested: more }).catch((e) => toast.error(getErrorMessage(e, "Failed")))}
                    testidBase="catalog"
                  />
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SizeInlineRow({ size, qty, moreRequested = false, onSet, testidBase }) {
  const activePrice = priceForQty(size.tier_breaks, qty || 1);
  return (
    <div className="border-t border-[#E2E4E0] pt-2 first:border-t-0 first:pt-0" data-testid={`${testidBase}-size-${size.size_variant_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">
            {size.size_name}
            {size.size_note && <span className="ml-1 text-[11px] text-[#5C665A]/80 font-normal">({size.size_note})</span>}
          </div>
          <div className="text-[11px] text-[#5C665A] db-mono">{size.sku}</div>
        </div>
        <div className="text-right">
          <div className="db-mono text-base font-semibold" data-testid={`${testidBase}-price-${size.size_variant_id}`}>{fmtEur(activePrice)}</div>
          {size.is_partner_priced && (
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8A5A2B]" data-testid={`${testidBase}-partner-${size.size_variant_id}`}>Partner price</div>
          )}
          <StockPill size={size} />
        </div>
      </div>
      {!size.is_partner_priced && <TierBreaksLine breaks={size.tier_breaks} />}
      <div className="flex items-center justify-between mt-1.5">
        <div />
        <QtyStepper size={size} current={qty} onChange={onSet} testidBase={testidBase} moreRequested={moreRequested} />
      </div>
    </div>
  );
}

/* -------------------- Table view -------------------- */

function TableView({ products, cartQty, cartItems, onSet }) {
  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden" data-testid="catalog-table-view">
      <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-[#F9F9F8] text-[#5C665A]">
          <tr>
            <th className="text-left px-3 py-2 db-h font-semibold w-14"></th>
            <th className="text-left px-3 py-2 db-h font-semibold">Product</th>
            <th className="text-left px-3 py-2 db-h font-semibold w-64 hidden lg:table-cell">Parents</th>
            <th className="text-left px-3 py-2 db-h font-semibold">Sizes · price · qty</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr key={p.id} className="border-t border-[#E2E4E0] db-row align-top" data-testid={`catalog-row-${p.id}`}>
              <td className="px-3 py-3">
                <Link to={`/catalog/${p.id}`} className="block">
                  <div className="w-14 h-14 rounded-md border border-[#E2E4E0] bg-[#F9F9F8] overflow-hidden">
                    {p.photos?.[0] ? <img src={fileUrl(p.photos[0].storage_path)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                </Link>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-start gap-2 flex-wrap">
                  <Link to={`/catalog/${p.id}`} className="db-h font-semibold hover:underline break-words min-w-0" data-testid={`catalog-name-${p.id}`}>{p.name}</Link>
                  {p.is_own_cross && <Badge className="bg-[#E9F5EC] text-[#1B4332] border-transparent shrink-0">Cross</Badge>}
                  <PropagationBadge type={p.propagation_type} size="xs" testid={`catalog-row-propagation-${p.id}`} className="shrink-0" />
                </div>
                {/* On <lg the Parents column is hidden — fall back to the inline lineage line */}
                <div className="lg:hidden">
                  <LineageLine p={p} />
                </div>
              </td>
              <td className="px-3 py-3 hidden lg:table-cell align-top">
                <ParentsCell p={p} />
              </td>
              <td className="px-3 py-3">
                <div className="space-y-2">
                  {p.sizes.map((s) => {
                    const cartItem = cartItems.find((i) => i.size_variant_id === s.size_variant_id);
                    return (
                      <SizeInlineRow
                        key={s.size_variant_id} size={s}
                        qty={cartQty[s.size_variant_id] || 0}
                        moreRequested={!!cartItem?.more_requested}
                        onSet={(q, more) => onSet(s.size_variant_id, q, { more_requested: more }).catch((e) => toast.error(getErrorMessage(e, "Failed")))}
                        testidBase="catalog"
                      />
                    );
                  })}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

/* -------------------- Parents cell (table view) -------------------- */

function ParentsCell({ p }) {
  if (!p.is_own_cross || (!p.seed_parent && !p.pollen_parent)) {
    return <span className="text-xs text-[#8A9088]" data-testid={`catalog-parents-${p.id}-empty`}>—</span>;
  }
  const roles = p.matched_parent_roles || [];
  const link = (parent) => {
    if (!parent) return <span className="text-[#8A9088]">—</span>;
    if (parent.linked_product_id) {
      return <Link to={`/catalog/${parent.linked_product_id}`} className="underline underline-offset-2 hover:text-[#1B4332]" data-testid={`catalog-parent-link-${parent.linked_product_id}`}>{parent.name}</Link>;
    }
    return <span>{parent.name}</span>;
  };
  return (
    <div className="text-xs text-[#1A1D1A] space-y-1" data-testid={`catalog-parents-${p.id}`}>
      <div className="flex items-start gap-1.5">
        <RoleTag role="seed" active={roles.includes("seed")} />
        <span className="min-w-0 break-words">{link(p.seed_parent)}</span>
      </div>
      <div className="flex items-start gap-1.5">
        <RoleTag role="pollen" active={roles.includes("pollen")} />
        <span className="min-w-0 break-words">{link(p.pollen_parent)}</span>
      </div>
    </div>
  );
}

/* -------------------- Floating cart summary -------------------- */

// Extracted to /components/FloatingCartSummary.jsx so ProductDetail can reuse.
